import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewRecordPage = () => {
  const navigate = useNavigate();
  const [structures, setStructures] = useState([]);
  const [selectedStructure, setSelectedStructure] = useState('');
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/data-structures`).then(res => {
      setStructures(res.data);
      if (res.data.length > 0) {
        setSelectedStructure(res.data[0].id);
      }
    }).catch(() => toast.error('Failed to load data structures'));
  }, []);

  const activeStructure = structures.find(s => s.id === selectedStructure);
  const fields = (activeStructure?.fields || []).filter(f => f.visible !== false);

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStructure) {
      toast.error('Please select a data structure');
      return;
    }

    // Validate required fields
    const missing = fields.filter(f => f.required && (!formData[f.name] || String(formData[f.name]).trim() === ''));
    if (missing.length > 0) {
      toast.error(`Required fields missing: ${missing.map(f => f.name).join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/records`, {
        structure_id: selectedStructure,
        data: formData
      });
      toast.success('Record created');
      navigate(`/records/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create record');
    } finally {
      setSubmitting(false);
    }
  };

  const renderInput = (field) => {
    const val = formData[field.name] ?? '';
    const onChange = (v) => handleFieldChange(field.name, v);

    switch (field.type) {
      case 'date':
        return <Input type="date" value={val} onChange={e => onChange(e.target.value)} data-testid={`field-${field.name}`} />;
      case 'number':
      case 'decimal':
        return <Input type="number" value={val} onChange={e => onChange(e.target.value)} data-testid={`field-${field.name}`} />;
      case 'email':
        return <Input type="email" value={val} onChange={e => onChange(e.target.value)} data-testid={`field-${field.name}`} />;
      case 'phone':
        return <Input type="tel" value={val} onChange={e => onChange(e.target.value)} data-testid={`field-${field.name}`} />;
      case 'comments':
        return <Textarea value={val} onChange={e => onChange(e.target.value)} rows={3} data-testid={`field-${field.name}`} />;
      case 'radio':
        return (
          <select
            value={val}
            onChange={e => onChange(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid={`field-${field.name}`}
          >
            <option value="">Select...</option>
            {(field.options || []).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'multi_select': {
        const selected = Array.isArray(val) ? val : [];
        return (
          <div className="space-y-1.5" data-testid={`field-${field.name}`}>
            {(field.options || []).map(opt => (
              <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={selected.includes(opt)}
                  onCheckedChange={(checked) => {
                    const updated = checked ? [...selected, opt] : selected.filter(v => v !== opt);
                    onChange(updated);
                  }}
                />
                {opt}
              </label>
            ))}
          </div>
        );
      }
      default:
        return <Input value={val} onChange={e => onChange(e.target.value)} data-testid={`field-${field.name}`} />;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/records')} data-testid="back-btn">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">New Record</h1>
          <p className="text-muted-foreground text-sm">Create a new customer record</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Select Schema</CardTitle>
          <CardDescription>Choose the data structure for this record</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {structures.map(s => (
              <button
                key={s.id}
                onClick={() => { setSelectedStructure(s.id); setFormData({}); }}
                className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${selectedStructure === s.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary/50 border-border hover:bg-secondary'}`}
                data-testid={`schema-option-${s.id}`}
              >
                {s.name}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {activeStructure && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{activeStructure.name} Fields</CardTitle>
            <CardDescription>Fill in the record details</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {fields.map(field => (
                  <div key={field.name} className="space-y-1.5">
                    <Label className="text-sm">
                      {field.name}
                      {field.required && <span className="text-destructive ml-0.5">*</span>}
                    </Label>
                    {renderInput(field)}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => navigate('/records')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="create-record-btn">
                  {submitting ? 'Creating...' : 'Create Record'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NewRecordPage;
